// analytics/tabs/LotsTrendsTab.jsx - Updated with unified filters

import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
// import inferenceService from '../../../js/Inspection/services/inferenceService.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';
import { TrendChart } from '../components/charts/TrendChart.jsx';

// Use the unified filter hook and components
import { useTabFilters } from '../hooks/useTabFilters.js';
import { UnifiedFilterPanel } from '../components/filters/UnifiedFilterComponents.jsx';

// Import the reusable inspection components
import {
    InspectionTable,
    InspectionDetailsModal,
    ImageModal
} from '../components/InspectionComponents.jsx';

import {
    LotTableRow,
    LotDetailsModal
} from '../components/LotComponents.jsx';

export const LotsTrendsTab = () => {
    const { state, setError, addNotification } = useAppContext();
    const { itemsPerPage, includeManualImages } = state.preferences;

    // Use unified filters with lot-specific defaults
    const {
        filters,
        handleFilterChange,
        resetFilters,
        getConfiguredFilters,
        hasInitialized
    } = useTabFilters(
        {
            sortBy: 'last_run_at' // Lot-specific default
        },
        {
            onPageReset: () => setPagination(prev => ({ ...prev, page: 1 }))
        }
    );

    // State for filter dropdown options
    const [availableOptions, setAvailableOptions] = useState({ projects: [] });
    
    // State for lots and pagination
    const [lots, setLots] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [isLoading, setIsLoading] = useState(true);

    // State for lot details modal
    const [showLotDetailsModal, setShowLotDetailsModal] = useState(false);
    const [detailsLot, setDetailsLot] = useState(null);
    const [trendData, setTrendData] = useState([]);
    const [isTrendLoading, setIsTrendLoading] = useState(false);
    const [recentInspections, setRecentInspections] = useState([]);
    const [isInspectionsLoading, setIsInspectionsLoading] = useState(false);
    
    const [lotInspPagination, setLotInspPagination] = useState({ 
        page: 1, 
        limit: itemsPerPage || 10, 
        total: 0, 
        totalPages: 1 
    });

    // State for nested inspection components
    const [modelList, setModelList] = useState([]);
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [showInspectionDetails, setShowInspectionDetails] = useState(false);
    const [largeImage, setLargeImage] = useState(null);
    const [isGroundTruthLoading, setIsGroundTruthLoading] = useState(false);

    // Configuration Sync Effect
    useEffect(() => {
        setLotInspPagination(prev => ({
            ...prev,
            limit: itemsPerPage || 10
        }));
    }, [itemsPerPage]);

    // Load initial data
    useEffect(() => {
        const fetchModelList = async () => {
            try {
                // const models = await inferenceService.fetchAvailableModels(null);
                const models = await api.getAvailableModels(null);
                setModelList(models || []);
            } catch (error) {
                addNotification({ type: 'warning', message: 'Could not load model names.' });
            }
        };
        fetchModelList();
    }, [addNotification]);

    // Load projects for filter options
    useEffect(() => {
        api.getProjects()
            .then(res => {
                if (Array.isArray(res)) {
                    setAvailableOptions(prev => ({ ...prev, projects: res.map(p => p.name) }));
                }
            })
            .catch(err => console.error("[LotsTrendsTab] Failed to fetch projects for filters:", err));
    }, []);

    // Load lots when filters change
    const loadLots = useCallback(async (page) => {
        if (!hasInitialized) return;

        try {
            setIsLoading(true);
            setError(null);
            
            const configuredFilters = getConfiguredFilters();
            const response = await api.getLots(configuredFilters, page, itemsPerPage || 20);
            setLots(response.data || []);
            setPagination({
                page: response.page,
                totalPages: response.totalPages,
                total: response.total
            });
        } catch (error) {
            setError(error);
            setLots([]);
        } finally {
            setIsLoading(false);
        }
    }, [setError, getConfiguredFilters, itemsPerPage, hasInitialized]);

    // Reload lots when filters or page changes
    useEffect(() => {
        loadLots(pagination.page);
    }, [loadLots, pagination.page]);

    // Page change handler
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.page) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    // Modal handlers
    const handleViewLotDetails = async (lot) => {
        setDetailsLot(lot);
        setShowLotDetailsModal(true);
        
        setIsTrendLoading(true);
        setIsInspectionsLoading(true);
        
        try {
            const configuredFilters = getConfiguredFilters();
            const inspectionsFilter = {
                lotId: lot.lot_identifier,
                project: lot.project_name,
                ...configuredFilters,
                sortBy: 'inspection_time',
                sortDirection: 'desc'
            };

            const [trendResult, inspectionsResult] = await Promise.all([
                api.getLotTrend(lot.lot_identifier, filters.dateRange),
                api.getInspections(inspectionsFilter, 1, lotInspPagination.limit)
            ]);
            
            setTrendData(trendResult || []);
            setRecentInspections(inspectionsResult?.data || []);
            setLotInspPagination(prev => ({
                ...prev,
                page: inspectionsResult?.page || 1,
                total: inspectionsResult?.total || 0,
                totalPages: inspectionsResult?.total_pages || 1
            }));

        } catch (error) {
            addNotification({ type: 'danger', message: `Failed to load lot details: ${error.message}` });
            setTrendData([]);
            setRecentInspections([]);
        } finally {
            setIsTrendLoading(false);
            setIsInspectionsLoading(false);
        }
    };

    const handleCloseLotModal = () => {
        setShowLotDetailsModal(false);
        setDetailsLot(null);
        setTrendData([]);
        setRecentInspections([]);
        setLotInspPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 1 }));
    };

    const handleLotInspectionPageChange = (newPage) => {
        if (detailsLot && newPage >= 1 && newPage <= lotInspPagination.totalPages) {
            setIsInspectionsLoading(true);
            const configuredFilters = getConfiguredFilters();
            const inspectionsFilter = {
                lotId: detailsLot.lot_identifier,
                project: detailsLot.project_name,
                ...configuredFilters,
                sortBy: 'inspection_time',
                sortDirection: 'desc'
            };
            api.getInspections(inspectionsFilter, newPage, lotInspPagination.limit)
                .then(res => {
                    setRecentInspections(res.data || []);
                    setLotInspPagination(prev => ({ ...prev, page: res.page || newPage }));
                })
                .catch(err => addNotification({ type: 'danger', message: `Failed to load page: ${err.message}` }))
                .finally(() => setIsInspectionsLoading(false));
        }
    };

    const handleViewInspectionDetails = useCallback((inspection) => {
        setSelectedInspection(inspection);
        setShowInspectionDetails(true);
    }, []);

    const handleCloseInspectionDetails = useCallback(() => {
        setShowInspectionDetails(false);
        setSelectedInspection(null);
    }, []);

    const handleImageClick = useCallback((imageUrl) => {
        setLargeImage(imageUrl);
    }, []);

    const handleCloseLargeImage = useCallback(() => {
        setLargeImage(null);
    }, []);
    
    const handleSetGroundTruth = useCallback(async (inspectionId, status) => {
        setIsGroundTruthLoading(true);
        try {
            const data = await api.confirmClassification(inspectionId, status);
            const updatedInspection = {
                confirmation_status: status,
                confirmation_timestamp: new Date().toISOString(),
                confirmed_by: data?.username || 'current user'
            };

            setRecentInspections(prev => prev.map(insp =>
                insp._id === inspectionId ? { ...insp, ...updatedInspection } : insp
            ));

            if (selectedInspection?._id === inspectionId) {
                setSelectedInspection(prev => ({ ...prev, ...updatedInspection }));
            }
            addNotification({ type: 'success', message: `Ground truth set as ${status}` });
        } catch (error) {
            addNotification({ type: 'danger', message: `Failed to update: ${error.message}` });
        } finally {
            setIsGroundTruthLoading(false);
        }
    }, [selectedInspection, addNotification]);

    if (!hasInitialized) {
        return <LoadingSpinner message="Initializing filters..." size="large" />;
    }

    return (
        <ErrorBoundary error={state.error} onRetry={() => loadLots(1)}>
            <div className="lots-content fade-in">
                <div className="mb-4">
                    <h4 className="mb-1">Lot ID Performance & Trends</h4>
                    <p className="text-muted mb-0">Track and analyze inspection data grouped by specific lot identifiers.</p>
                </div>

                <UnifiedFilterPanel 
                    filters={filters} 
                    onFilterChange={handleFilterChange}
                    availableOptions={availableOptions}
                    config={{ includeManualImages, itemsPerPage }}
                    tabType="lots"
                    showConfigInfo={true}
                />

                <div className="card shadow-sm">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-boxes text-primary me-2"></i>Lot ID Inspection Data
                        </h5>
                        <small className="text-muted">
                            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total lots)
                        </small>
                    </div>
                    <div className="card-body">
                        {isLoading ? (
                            <LoadingSpinner message="Loading lots..." />
                        ) : lots.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Lot ID</th>
                                            <th>Part</th>
                                            <th>Last Seen</th>
                                            <th>Inspections</th>
                                            <th>Defect Rate</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lots.map(lot => (
                                            <LotTableRow
                                                key={lot.lot_identifier}
                                                lot={lot}
                                                onViewDetails={handleViewLotDetails}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center p-5">
                                <i className="fas fa-boxes fa-4x text-muted mb-4"></i>
                                <h4 className="text-muted">No Lots Found</h4>
                                <p className="text-muted">No Lot IDs match the current filters.</p>
                                <button 
                                    className="btn btn-primary mt-3" 
                                    onClick={resetFilters}
                                    title="Reset all filters to defaults"
                                >
                                    <i className="fas fa-undo me-2"></i>Reset Filters
                                </button>
                            </div>
                        )}
                    </div>
                    {pagination.totalPages > 1 && (
                        <div className="card-footer">
                            <nav className="d-flex justify-content-center">
                                <ul className="pagination mb-0">
                                    <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                                        <button 
                                            className="page-link" 
                                            onClick={() => handlePageChange(pagination.page - 1)}
                                            disabled={pagination.page <= 1}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    <li className="page-item active">
                                        <span className="page-link">
                                            {pagination.page} of {pagination.totalPages}
                                        </span>
                                    </li>
                                    <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                                        <button 
                                            className="page-link" 
                                            onClick={() => handlePageChange(pagination.page + 1)}
                                            disabled={pagination.page >= pagination.totalPages}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>

                <LotDetailsModal
                    lot={detailsLot}
                    isOpen={showLotDetailsModal}
                    onClose={handleCloseLotModal}
                    trendData={trendData}
                    isTrendLoading={isTrendLoading}
                    recentInspections={recentInspections}
                    isInspectionsLoading={isInspectionsLoading}
                    onViewInspectionDetails={handleViewInspectionDetails}
                    onImageClick={handleImageClick}
                    onSetGroundTruth={handleSetGroundTruth}
                    isGroundTruthLoading={isGroundTruthLoading}
                    addNotification={addNotification}
                    inspectionPagination={lotInspPagination}
                    onInspectionPageChange={handleLotInspectionPageChange}
                    modelList={modelList}
                    filters={filters} 
                />

                {showInspectionDetails && selectedInspection && (
                    <InspectionDetailsModal
                        inspection={selectedInspection}
                        modelList={modelList}
                        onClose={handleCloseInspectionDetails}
                        onImageClick={handleImageClick}
                        onSetGroundTruth={handleSetGroundTruth}
                        isLoading={isGroundTruthLoading}
                        addNotification={addNotification}
                    />
                )}

                <ImageModal
                    src={largeImage}
                    alt="Enlarged view"
                    show={!!largeImage}
                    onClose={handleCloseLargeImage}
                />
            </div>
        </ErrorBoundary>
    );
};
